/* Annam Section – Sacred Modern Design
 * Light section with Annam as the star
 * Her story, her roots, her mission
 * Large portrait, emotional quote, background
 */
import { IMAGES } from "@/lib/constants";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Quote } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

export default function AnnamSection() {
  const { t } = useI18n();
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="annam" className="section-light py-24 lg:py-32 relative overflow-hidden" ref={ref}>
      <div className="gold-line w-full absolute top-0" />

      <div className="container">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
          {/* Left: Large Portrait (2 cols) */}
          <div
            className={`lg:col-span-2 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <div className="relative pb-10">
              <div className="absolute -inset-3 bg-[oklch(0.78_0.11_85/0.08)] rounded-3xl blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden border-2 border-[oklch(0.78_0.11_85/0.25)] shadow-xl">
                <img
                  src={IMAGES.annamOrange}
                  alt="Annam Hanfstein"
                  className="w-full h-auto object-cover aspect-[3/4]"
                />
              </div>
              {/* Name badge */}
              <div className="absolute -bottom-4 left-4 right-4 bg-white rounded-xl p-4 shadow-lg border border-[oklch(0.88_0.02_80)]">
                <p className="font-bold text-[oklch(0.25_0.04_50)] text-lg" style={{ fontFamily: "var(--font-display)" }}>
                  {t("landing.annam.annamTitle")}
                </p>
                <p className="text-sm text-[oklch(0.52_0.09_128)]">
                  {t("landing.annam.annamRole")}
                </p>
              </div>
            </div>
          </div>

          {/* Right: Story (3 cols) */}
          <div
            className={`lg:col-span-3 transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)]" style={{ fontFamily: "var(--font-body)" }}>
              {t("landing.annam.badge")}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.25_0.04_50)] mt-4 mb-8">
              {t("landing.annam.heading1")} <span className="text-[oklch(0.52_0.09_128)]">{t("landing.annam.heading2")}</span>.
            </h2>

            {/* Quote */}
            <div className="relative mb-8 pl-6 border-l-2 border-[oklch(0.78_0.11_85)]">
              <Quote className="absolute -left-3 -top-1 text-[oklch(0.52_0.09_128)] bg-[oklch(0.97_0.01_80)]" size={20} />
              <p className="text-xl italic text-[oklch(0.35_0.04_50)] leading-relaxed" style={{ fontFamily: "var(--font-display)" }}>
                {t("landing.annam.quote")}
              </p>
            </div>

            <div className="space-y-4 text-[oklch(0.45_0.04_50)] leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
              <p>{t("landing.annam.p1")}</p>
              <p>{t("landing.annam.p2")}</p>
              <p>{t("landing.annam.p3")}</p>
            </div>

            {/* Chris mention */}
            <div className="mt-10 flex items-center gap-5 p-5 rounded-xl bg-[oklch(0.93_0.015_80)] border border-[oklch(0.88_0.02_80)]">
              <img
                src={IMAGES.chris}
                alt="Christopher Hanfstein"
                className="w-16 h-16 rounded-full object-cover border-2 border-[oklch(0.78_0.11_85/0.3)]"
              />
              <div>
                <p className="font-bold text-[oklch(0.3_0.04_50)]" style={{ fontFamily: "var(--font-display)" }}>
                  {t("landing.annam.chrisTitle")}
                </p>
                <p className="text-sm text-[oklch(0.5_0.03_50)]" style={{ fontFamily: "var(--font-body)" }}>
                  {t("landing.annam.chrisDesc")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
